import { abroad, homeCountry, type Country } from './travel.ts';
import { hereCountry, localPrice } from './character.ts';
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { Rng } from './rng.ts';
import { nameFor } from './spawn.ts';

/**
 * Events: things on in the city you can book and go to with other people (the Events app). The first is a sip and
 * paint: an evening at an art studio, a glass of wine, everyone at an easel painting the same picture with a host
 * talking you through it. The point is the people: the others there come over and talk to you (they make the first
 * move; you can chat, keep it short, or keep painting), and the ones you get on with become friends.
 *
 * Built so the same evening can be a private one (a date at the studio, with an NPC or another player): the studio,
 * the easels, the reach-outs don't care who's in the room.
 */

export type EventKind = 'sippaint';
export interface EventBooking { id: string; kind: EventKind; at: number; venue: string; state: 'booked' | 'done' | 'missed' }
export interface EventSession { kind: EventKind; label: string; blurb: string; at: number; venue: string; price: number; booked: boolean; full?: boolean }

const DAY = 1440;
/** sessions: Thursday, Friday, Saturday evenings, 18:30, three hours (day 0 of a life is a Monday) */
const NIGHTS = [3, 4, 5], START = 18 * 60 + 30, LENGTH = 180;
/** made-up studios, one per city (real studios are businesses: same rule as the clubs and the car makes) */
const STUDIOS: Record<string, string> = {
  nigeria: 'Canvas & Cork, Lekki', france: "L'Atelier du Soir, Paris 11e", usa: 'Brush & Barrel, Brooklyn', china: 'Ink & Grape, Jing\'an', japan: 'Atelier Yoru, Shimokitazawa',
};
const REGION_OF: Record<Country, string> = { nigeria: 'lagos', france: 'france', usa: 'us', china: 'china', japan: 'japan', italy: 'italy', uae: 'uae' };

export function eventsOf(s: LifeState): EventBooking[] { return (s.events ??= []); }
export const studioHere = (s: LifeState) => STUDIOS[hereCountry(s)] ?? STUDIOS.nigeria;
/** the ticket: about ₦18,000 in Lagos (wine, canvas, paints, the host), the local equivalent elsewhere */
export const sipPaintPrice = (s: LifeState) => localPrice(s, 18_000, 500);

/** The sessions in the next week, booked or not. */
export function eventsAhead(s: LifeState): EventSession[] {
  const today = Math.floor(s.minutes / DAY), out: EventSession[] = [], venue = studioHere(s);
  for (let d = today; d < today + 8; d++) {
    if (!NIGHTS.includes(d % 7)) continue;
    const at = d * DAY + START;
    if (at + LENGTH < s.minutes) continue;
    out.push({ kind: 'sippaint', label: 'Sip & Paint', blurb: 'An easel, a glass of wine, a room of strangers painting the same picture. The host talks you through it.', at, venue, price: sipPaintPrice(s), booked: eventsOf(s).some((b) => b.at === at && b.state === 'booked') });
  }
  return out;
}

export function bookEvent(s: LifeState, at: number): LifeEvent[] | null {
  const sess = eventsAhead(s).find((e) => e.at === at);
  if (!sess || sess.booked || !spend(s, sess.price, `${sess.label}, ${sess.venue.split(',')[0]}`)) return null;
  eventsOf(s).push({ id: `ev-${at}`, kind: sess.kind, at, venue: sess.venue, state: 'booked' });
  return [{ t: s.minutes, kind: 'info', text: `Booked: ${sess.label} at ${sess.venue.split(',')[0]}.` }];
}

/** A booking you can go to now (from a quarter of an hour before till an hour before the end). */
export function eventNow(s: LifeState): EventBooking | null {
  return eventsOf(s).find((b) => b.state === 'booked' && s.minutes >= b.at - 15 && s.minutes <= b.at + LENGTH - 60) ?? null;
}
export const eventsBooked = (s: LifeState) => eventsOf(s).filter((b) => b.state === 'booked' && b.at + LENGTH > s.minutes).sort((a, b) => a.at - b.at);
export const EVENT_LENGTH = LENGTH;

/** Evenings that came and went without you: missed (a ticket wasted). Called each day. */
export function eventsDay(s: LifeState) {
  for (const b of eventsOf(s)) if (b.state === 'booked' && s.minutes > b.at + LENGTH) b.state = 'missed';
}

// --- the people there ---------------------------------------------------------------------------------------------------

/** someone at the next easel: who they are, how they open, what they'd talk about */
export interface Attendee { name: string; sex: 'm' | 'f'; job: string; opener: string; topic: string; warmth: number }
const JOBS: Record<Country, string[]> = {
  nigeria: ['a nurse at LUTH', 'a banker on the Island', 'a UX designer', 'a law student', 'an Uber driver saving for a car', 'a baker with an Instagram page', 'an accountant', 'a teacher'],
  france: ['une infirmière', 'a sound engineer', 'a pastry chef', 'a lawyer', 'a student at the Beaux-Arts', 'a bookseller', 'a nurse', 'a web designer'],
  uae: ['a pilot for an airline', 'a hotel manager', 'a real-estate broker', 'an engineer on a tower', 'a nurse', 'a chef at a brunch place', 'a content creator', 'a flight attendant'],
  italy: ['a restorer at a museum', 'a sommelier', 'a leather artisan', 'an architect', 'a student at the Accademia', 'a tour guide', 'a nurse', 'a gelato maker'],
  usa: ['a nurse', 'a software engineer', 'a barista and actor', 'a paralegal', 'a teacher', 'a grad student', 'a dental hygienist', 'a photographer'],
  china: ['an accountant', 'a product manager', 'a teacher', 'a nurse', 'a designer', 'a student', 'a translator', 'a chef'],
  japan: ['an office worker', 'a nurse', 'a designer', 'a student', 'a hairdresser', 'a teacher', 'an engineer', 'a café owner'],
};
const OPENERS = [
  (n: string) => `"Is this your first one? Mine already looks like a potato," ${n} says, leaning over from the next easel.`,
  (n: string) => `${n} tops up your glass without asking. "You look like you know what you're doing. Do you?"`,
  (n: string) => `"Okay, I need a second opinion," ${n} says, turning their canvas towards you. "Is that a sunset or a fried egg?"`,
  (n: string) => `${n} catches your eye across the easels and comes over with the wine. "I'm here because my friend cancelled. You?"`,
  (n: string) => `"Can I borrow your yellow? Mine's finished," ${n} says. Then: "Wait, that's actually good."`,
];
const TOPICS = ['what they do for work', 'the worst date they\'ve been on', 'where to get the best food round here', 'a trip they want to take', 'the last film that made them cry', 'their plan to quit and start a business'];

/** The night's crowd: the same people for the same session (four of them, a mix), made from the session and your life. */
export function attendeesOf(s: LifeState, b: { at: number }): Attendee[] {
  const c = hereCountry(s), r = new Rng((s.seed ^ Math.imul(b.at + 7, 0x9e3779b1)) >>> 0), out: Attendee[] = [];
  const opens = [...OPENERS].sort(() => r.next() - 0.5);
  for (let i = 0; i < 4; i++) {
    const sex: 'm' | 'f' = i % 2 === 0 ? 'f' : 'm'; // (half and half: it's a sip and paint, not a stag do)
    const name = nameFor(REGION_OF[c], sex, r);
    out.push({ name, sex, job: r.pick(JOBS[c]), opener: opens[i % opens.length](name.split(' ')[0]), topic: r.pick(TOPICS), warmth: 0.45 + r.next() * 0.45 });
  }
  return out;
}

/**
 * They came over. `how`: you chatted back, kept it short, or kept painting (ignored: or let the moment pass). Chatting
 * is social and fun, and if it goes well (how warm they are, how good you are with people) they ask to swap numbers:
 * `asks`. Saying yes is `swapNumbers`.
 */
export function answerApproach(s: LifeState, a: Attendee, how: 'chat' | 'short' | 'ignore'): { line: string; asks: boolean; events: LifeEvent[] } {
  const first = a.name.split(' ')[0], events = advance(s, how === 'chat' ? 15 : 3);
  if (how === 'ignore') return { line: `You smile and go back to your canvas. ${first} drifts back to their easel.`, asks: false, events };
  s.needs.social = Math.min(100, s.needs.social + (how === 'chat' ? 14 : 5));
  s.needs.fun = Math.min(100, s.needs.fun + (how === 'chat' ? 6 : 2));
  s.skills.social = Math.min(100, s.skills.social + (how === 'chat' ? 0.4 : 0.1));
  s.stats.strangersTalked++;
  if (how === 'short') return { line: `A minute about the painting, then ${first} lets you get on with it.`, asks: false, events };
  const known = s.contacts.some((x) => x.name === a.name);
  const r = new Rng((s.seed ^ Math.imul(s.minutes + a.name.length, 0x85ebca6b)) >>> 0);
  return { line: `You talk about ${a.topic}. Time goes quickly.`, asks: !known && r.next() < a.warmth + s.skills.social / 250, events };
}

/** You said yes: a new friend in Contacts. */
export function swapNumbers(s: LifeState, a: Attendee): LifeEvent[] {
  if (s.contacts.some((x) => x.name === a.name)) return [];
  s.contacts.push({ name: a.name, relation: 'Friend', closeness: 28, met: s.minutes });
  s.history.push({ t: s.minutes, text: `Met ${a.name} (${a.job}) at a sip and paint.` });
  return [{ t: s.minutes, kind: 'info', text: `${a.name} is in your Contacts now.` }];
}

/** The evening's done: the ticket used, a little more creative, a good tired. */
export function finishEvent(s: LifeState, b: EventBooking): LifeEvent[] {
  b.state = 'done';
  s.skills.creativity = Math.min(100, s.skills.creativity + 1.2);
  s.needs.fun = Math.min(100, s.needs.fun + 10);
  return [{ t: s.minutes, kind: 'info', text: 'A good evening. You take your painting home, still a bit wet.' }];
}

void abroad; void homeCountry;
